// Rules of the page "ajouter une société et son accès administrateur" (/addAdminSociete).
// The email and the password follow the rules of the profile service (JwtUserDetailsService); the formats of
// the matricule, the CIN and the phone are the usual tunisian ones, change the constants to loosen them.

export const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const MIN_PASSWORD_LENGTH = 8;
// matricule fiscal : 7 digits and its control letter (the letter may be left out)
export const MATRICULE_REGEX = /^\d{7}[A-Za-z]?$/;
// carte d'identité nationale : 8 digits
export const CIN_REGEX = /^\d{8}$/;
export const PHONE_REGEX = /^\+?[0-9 ()-]{8,20}$/;
// number of secondary establishments, written on 3 digits (000, 001...)
export const ESTABLISHMENTS_REGEX = /^\d{3}$/;

const blank = (value) => value === undefined || value === null || String(value).trim() === "";

const isNumber = (value) => !blank(value) && !isNaN(Number(value));

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// id: the id of the field in error (the page shows `short` under it), message: the same with who it is about
const error = (id, who, phrase) => ({ id, who, short: capitalize(phrase), message: `${who} : ${phrase}` });

/** The errors of a person's access (admin or warehouse manager); `prefix` is the prefix of the field ids. */
const personErrors = (person, who, prefix) => {
  const errors = [];
  if (blank(person.nom)) errors.push(error(`${prefix}-nom`, who, "le nom est obligatoire"));
  if (blank(person.prenom)) errors.push(error(`${prefix}-prenom`, who, "le prénom est obligatoire"));
  if (blank(person.cin)) {
    errors.push(error(`${prefix}-cin`, who, "le CIN est obligatoire"));
  } else if (!CIN_REGEX.test(person.cin.trim())) {
    errors.push(error(`${prefix}-cin`, who, "le CIN doit contenir 8 chiffres"));
  }
  if (blank(person.email)) {
    errors.push(error(`${prefix}-email`, who, "l'email est obligatoire"));
  } else if (!EMAIL_REGEX.test(person.email.trim())) {
    errors.push(error(`${prefix}-email`, who, "l'email n'est pas valide"));
  }
  if (blank(person.phone)) {
    errors.push(error(`${prefix}-phone`, who, "le téléphone est obligatoire"));
  } else if (!PHONE_REGEX.test(person.phone.trim())) {
    errors.push(error(`${prefix}-phone`, who, "le téléphone n'est pas valide"));
  }
  if (blank(person.password)) {
    errors.push(error(`${prefix}-password`, who, "le mot de passe est obligatoire"));
  } else if (person.password.length < MIN_PASSWORD_LENGTH) {
    errors.push(
      error(`${prefix}-password`, who, `le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères`)
    );
  } else if (person.password !== person.rePassword) {
    errors.push(error(`${prefix}-rePassword`, who, "le mot de passe n'est pas confirmé"));
  }
  return errors;
};

/**
 * Every error of the form, in the order of the screen, with the id of the field concerned ([] when it can be
 * submitted). values: { societe: {...}, file, admin: {...}, manager: {...} | null }
 */
export const validateSocieteFormByField = (values) => {
  const { societe, file, admin, manager } = values;
  const errors = [];

  if (blank(societe.title)) errors.push(error("societe-title", "Société", "le titre est obligatoire"));
  if (blank(societe.matricule)) {
    errors.push(error("societe-matricule", "Société", "le matricule est obligatoire"));
  } else if (!MATRICULE_REGEX.test(societe.matricule.trim())) {
    errors.push(
      error("societe-matricule", "Société", "le matricule doit contenir 7 chiffres suivis d'une lettre (ex. 1234567A)")
    );
  }
  if (blank(societe.numberEtabSecondaire)) {
    errors.push(
      error("societe-etablissements", "Société", "le nombre d'établissements secondaires est obligatoire")
    );
  } else if (!ESTABLISHMENTS_REGEX.test(societe.numberEtabSecondaire.trim())) {
    errors.push(
      error(
        "societe-etablissements",
        "Société",
        "le nombre d'établissements secondaires doit contenir 3 chiffres (ex. 000)"
      )
    );
  }
  if (!isNumber(societe.timbre) || Number(societe.timbre) < 0) {
    errors.push(error("societe-timbre", "Société", "le timbre doit être un nombre positif ou nul"));
  }
  if (blank(societe.email)) {
    errors.push(error("societe-email", "Société", "l'email est obligatoire"));
  } else if (!EMAIL_REGEX.test(societe.email.trim())) {
    errors.push(error("societe-email", "Société", "l'email n'est pas valide"));
  }
  if (blank(societe.phone)) {
    errors.push(error("societe-phone", "Société", "le téléphone est obligatoire"));
  } else if (!PHONE_REGEX.test(societe.phone.trim())) {
    errors.push(error("societe-phone", "Société", "le téléphone n'est pas valide"));
  }
  if (blank(societe.adresse)) errors.push(error("societe-adresse", "Société", "l'adresse est obligatoire"));
  if (blank(societe.codeTVA)) errors.push(error("societe-code-tva", "Société", "le code TVA est obligatoire"));
  if (blank(societe.typeTVA)) errors.push(error("societe-type-tva", "Société", "le type de TVA est obligatoire"));
  if (!isNumber(societe.tva) || Number(societe.tva) < 0 || Number(societe.tva) > 100) {
    errors.push(error("societe-tva", "Société", "le taux de TVA ( % ) doit être compris entre 0 et 100"));
  }
  if (!file) errors.push(error("societe-image", "Société", "l'image est obligatoire"));

  errors.push(...personErrors(admin, "Admin", "admin"));

  if (manager) {
    errors.push(...personErrors(manager, "Responsable dépôt", "manager"));
    if (
      !blank(manager.email) &&
      !blank(admin.email) &&
      manager.email.trim().toLowerCase() === admin.email.trim().toLowerCase()
    ) {
      errors.push(error("manager-email", "Responsable dépôt", "l'email doit être différent de celui de l'admin"));
    }
  }
  return errors;
};

/** The messages of the errors of the form, in the order of the screen. */
export const validateSocieteForm = (values) => validateSocieteFormByField(values).map((e) => e.message);

/**
 * What is created by default for the new company: { centralWarehouse, catalog, managerAccess }. Asking for the
 * central warehouse asks for its manager access too (it can still be unticked: the admin then manages the
 * warehouse); the other choices are independent.
 */
export const applyDefaultsChoice = (defaults, field, checked) => {
  const next = { ...defaults, [field]: checked };
  if (field === "centralWarehouse" && checked) {
    next.managerAccess = true;
  }
  return next;
};
